import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import client from "@/lib/apiClient";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Lu, Loader } from "@/components/shared";
import { GuestConcierge } from "@/components/GuestConcierge";

function Dial({ temp, onChange }) {
  const min = 16, max = 30;
  const pct = (temp - min) / (max - min);
  const angle = -135 + pct * 270;
  return (
    <div className="relative w-52 h-52 mx-auto">
      <svg viewBox="0 0 200 200" className="w-full h-full -rotate-[135deg]">
        <circle cx="100" cy="100" r="86" fill="none" stroke="hsl(var(--md-soft))" strokeWidth="14" strokeDasharray="405 540" strokeLinecap="round" />
        <circle cx="100" cy="100" r="86" fill="none" stroke="#1E5EFF" strokeWidth="14" strokeDasharray={`${pct * 405} 540`} strokeLinecap="round" style={{ transition: "stroke-dasharray 0.3s" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-5xl font-extrabold font-mono text-foreground">{temp}°</span>
        <span className="text-xs text-primary mt-1">Target</span>
      </div>
      <button data-testid="temp-down" onClick={() => onChange(Math.max(min, temp - 1))}
        className="absolute bottom-2 ltr:left-2 rtl:right-2 w-11 h-11 rounded-full bg-foreground/5 border border-border flex items-center justify-center text-foreground/80 active:scale-90 transition-transform"><Lu name="minus" className="w-5 h-5" /></button>
      <button data-testid="temp-up" onClick={() => onChange(Math.min(max, temp + 1))}
        className="absolute bottom-2 ltr:right-2 rtl:left-2 w-11 h-11 rounded-full md-gold-gradient flex items-center justify-center text-primary-foreground active:scale-90 transition-transform"><Lu name="plus" className="w-5 h-5" /></button>
    </div>
  );
}

function Toggle({ icon, label, active, onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className="flex flex-col items-center gap-2 p-4 rounded-2xl border transition-colors"
      style={{ background: active ? "rgba(30,94,255,0.12)" : "hsl(var(--md-soft))", borderColor: active ? "rgba(30,94,255,0.4)" : "hsl(var(--md-soft))" }}>
      <Lu name={icon} className="w-6 h-6" style={{ color: active ? "#1E5EFF" : "#64748B" }} />
      <span className="text-xs text-foreground/80">{label}</span>
    </button>
  );
}

export default function GuestApp({ standalone }) {
  const { t, lang, toggle } = useLang();
  const { user, logout } = useAuth();
  const [room, setRoom] = useState(null);
  const [scenes, setScenes] = useState([]);
  const [phoneTheme, setPhoneTheme] = useState(null);

  const load = () => client.get("/guest/room").then((r) => { setRoom(r.data.room); setScenes(r.data.scenes); });
  useEffect(() => { load(); }, []);

  const control = async (patch) => {
    setRoom((r) => ({ ...r, ...patch }));
    await client.post("/guest/control", { room_id: room.id, ...patch });
  };
  const service = async (kind) => {
    await client.post("/guest/service", { room_id: room.id, kind });
    toast.success(t("requestSent"));
    if (kind === "dnd" || kind === "mur") setRoom((r) => ({ ...r, dnd: kind === "dnd", mur: kind === "mur" }));
  };

  if (!room) return <div className={standalone ? "min-h-screen bg-background" : ""}><Loader /></div>;

  const Phone = (
    <div className={`${phoneTheme || ""} max-w-md mx-auto bg-card text-foreground rounded-3xl border border-primary/20 shadow-2xl overflow-hidden relative`} data-testid="guest-phone" style={{ colorScheme: phoneTheme || undefined }}>
      {/* status bar */}
      <div className="relative h-44 overflow-hidden">
        <img src="https://images.unsplash.com/photo-1605237165959-dcc784975d51?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" alt="room" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,18,36,0.3), rgba(10,18,36,0.95))" }} />
        <div className="relative z-10 p-5 flex items-start justify-between">
          <div>
            <p className="text-xs text-primary">{t("welcome")}</p>
            <p className="text-lg font-bold text-white">{room.guest_name || user.name}</p>
            <p className="text-xs text-white/80 mt-0.5">{t("room")} {room.number} · {t("floor")} {room.floor}</p>
          </div>
          <button onClick={toggle} className="px-2.5 py-1 rounded-lg bg-white/15 text-xs font-semibold text-white">{lang === "ar" ? "EN" : "ع"}</button>
        </div>
      </div>

      <div className="p-5 space-y-5 -mt-4 relative z-10">
        <GuestConcierge room={room} onRoom={setRoom} />
        {/* AC dial */}
        <div className="bg-white/[0.03] rounded-3xl p-5 border border-border">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-foreground/80 flex items-center gap-2"><Lu name="snowflake" className="w-4 h-4 text-blue-400" />{t("temperature")}</span>
            <button data-testid="ac-toggle" onClick={() => control({ ac_on: !room.ac_on })}
              className="text-xs px-3 py-1 rounded-full" style={{ background: room.ac_on ? "rgba(59,130,246,0.2)" : "hsl(var(--md-soft))", color: room.ac_on ? "#60A5FA" : "#64748B" }}>
              {room.ac_on ? t("on") : t("off")}
            </button>
          </div>
          <Dial temp={room.target_temp} onChange={(v) => control({ target_temp: v })} />
          <p className="text-center text-xs text-muted-foreground/70 mt-2">{lang === "ar" ? "الحالية" : "Current"}: {room.temp}°C</p>
        </div>

        {/* toggles */}
        <div className="grid grid-cols-3 gap-3">
          <Toggle testid="light-toggle" icon="lightbulb" label={t("lighting")} active={room.lights_on} onClick={() => control({ lights_on: !room.lights_on })} />
          <Toggle testid="curtain-toggle" icon="blinds" label={t("curtains")} active={room.curtain_open} onClick={() => control({ curtain_open: !room.curtain_open })} />
          <Toggle testid="key-toggle" icon="key-round" label={t("digitalKey")} active={!room.door_locked} onClick={() => { control({ door_locked: !room.door_locked }); toast.success(room.door_locked ? "🔓" : "🔒"); }} />
        </div>

        {/* scenes */}
        <div>
          <p className="text-sm font-semibold text-foreground/80 mb-3">{t("scenes")}</p>
          <div className="grid grid-cols-3 gap-3">
            {scenes.map((s) => (
              <button key={s.key} data-testid={`guest-scene-${s.key}`} onClick={() => { control({ scene: s.key }); toast.success(s[lang === "ar" ? "name_ar" : "name_en"]); }}
                className="flex flex-col items-center gap-1.5 p-3 rounded-2xl border transition-colors"
                style={{ background: room.scene === s.key ? "rgba(30,94,255,0.14)" : "hsl(var(--md-soft))", borderColor: room.scene === s.key ? "rgba(30,94,255,0.45)" : "hsl(var(--md-soft))" }}>
                <Lu name={s.icon} className="w-5 h-5" style={{ color: room.scene === s.key ? "#1E5EFF" : "#94A3B8" }} />
                <span className="text-[11px] text-foreground/80">{s[lang === "ar" ? "name_ar" : "name_en"]}</span>
              </button>
            ))}
          </div>
        </div>

        {/* services */}
        <div>
          <p className="text-sm font-semibold text-foreground/80 mb-3">{t("services")}</p>
          <div className="grid grid-cols-2 gap-3">
            {[["housekeeping", "brush-cleaning", t("housekeeping")], ["room_service", "concierge-bell", t("roomService")],
              ["dnd", "moon", t("dnd")], ["mur", "bed", t("mur")]].map(([k, ic, lbl]) => (
              <button key={k} data-testid={`service-${k}`} onClick={() => service(k)}
                className="flex items-center gap-2.5 p-3.5 rounded-2xl bg-white/[0.04] border border-border text-sm text-foreground/80 active:scale-95 transition-transform"
                style={(k === "dnd" && room.dnd) || (k === "mur" && room.mur) ? { borderColor: "rgba(30,94,255,0.5)", background: "rgba(30,94,255,0.1)" } : {}}>
                <Lu name={ic} className="w-4 h-4 text-primary" />{lbl}
              </button>
            ))}
          </div>
        </div>

        <button data-testid="checkout-btn" onClick={() => toast.success(t("checkout"))}
          className="w-full py-3.5 rounded-2xl md-gold-gradient text-primary-foreground font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform">
          <Lu name="door-open" className="w-4 h-4" />{t("checkout")}
        </button>
        {standalone && <button onClick={logout} className="w-full text-xs text-muted-foreground/70 py-2">{t("logout")}</button>}
      </div>
    </div>
  );

  if (standalone) {
    return <div className="min-h-screen bg-background py-6 px-4"><motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>{Phone}</motion.div></div>;
  }
  return (
    <div>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2"><Lu name="bed-single" className="w-6 h-6 text-primary" />{t("guestApp")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{lang === "ar" ? "معاينة تجربة الضيف (شبيهة بـ Tuya)" : "Guest experience preview (Tuya-like)"}</p>
        </div>
        <div className="flex items-center gap-1 p-1 rounded-lg bg-foreground/5 border border-border" data-testid="phone-theme-switch">
          {[["light", "sun", "فاتح", "Light"], ["dark", "moon", "داكن", "Dark"], [null, "monitor", "النظام", "System"]].map(([v, ic, a, e]) => (
            <button key={String(v)} data-testid={`phone-theme-${v || "system"}`} onClick={() => setPhoneTheme(v)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold ${phoneTheme === v ? "bg-primary text-primary-foreground shadow" : "text-muted-foreground hover:text-foreground"}`}>
              <Lu name={ic} className="w-3.5 h-3.5" />{lang === "ar" ? a : e}
            </button>
          ))}
        </div>
      </div>
      {Phone}
    </div>
  );
}
